import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { TaskDetails } from '../components/TaskDetails'
import { useProjects } from '../hooks/useProjects'
import { resolveTaskIdentifier } from '../utils/taskIdentifier'
import { TASK_DETAILS_LAYOUT } from '../constants'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import { useStatuses, useTasks } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { useTaskPageSetup } from '@/hooks'

// A human task ID such as TA3-7, as opposed to the task's UUID.
const TASK_IDENTIFIER_REGEX = /^[A-Z][A-Z0-9]*-\d+$/i

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/tasks/$taskId',
)({
  component: TaskDetailsPage,
})

/**
 * Individual task details page component for viewing and editing a specific task.
 * 
 * Provides a dedicated full-page view for managing individual tasks including:
 * - Complete task details editing interface
 * - Parent task context and navigation
 * - Status management integration
 * - Real-time data synchronization
 * - Loading and error states
 * 
 * Features:
 * - Full-screen task editing interface
 * - Parent task relationship display
 * - Status integration
 * - Real-time task data updates
 * - Task not found error handling
 * - Navigation integration with task sidebar
 * 
 * Route: `/$workspaceSlug/tasks/$taskId`, where `$taskId` is the task's UUID
 * or its project ID (e.g. TA3-7, case-insensitive).
 * 
 * @example
 * ```tsx
 * // Accessed via URL like: /my-workspace/tasks/task-uuid-123
 * // or: /my-workspace/tasks/TA3-7
 * ```
 */
function TaskDetailsPage() {
  useTaskPageSetup()

  const { taskId } = Route.useParams()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)

  // Phone ‹ (design 36b): back where you came from, or Inbox from a shared link.
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const navigate = useNavigate()
  const handleBack = useCallback(() => {
    if (canGoBack) router.history.back()
    else void navigate({ to: '/$workspaceSlug/tasks', params: { workspaceSlug: workspaceSlug ?? '' } })
  }, [canGoBack, router, navigate, workspaceSlug])
  const chrome = useMemo(() => ({ onBack: handleBack }), [handleBack])

  // TaskDetailsPage gets its own subscription for finding by ID
  const { tasks, isLoading: tasksLoading } = useTasks({ isDeleted: false })
  const tasksReady = !tasksLoading

  const { projects } = useProjects()

  const { statuses, isLoading: statusesLoading } = useStatuses()
  const statusesReady = !statusesLoading

  const task = useMemo(() => {
    if (!tasksReady) {
      return null
    }

    const byUuid = tasks.find((item) => item.id === taskId && !item.isDeleted)
    if (byUuid || !TASK_IDENTIFIER_REGEX.test(taskId)) {
      return byUuid ?? null
    }

    const wanted = taskId.toUpperCase()
    const projectKeyById = new Map(projects.map((p) => [p.id, p.projectKey]))
    return (
      tasks.find(
        (item) =>
          !item.isDeleted &&
          resolveTaskIdentifier(
            item,
            item.projectId ? projectKeyById.get(item.projectId) : null,
          ).toUpperCase() === wanted,
      ) ?? null
    )
  }, [tasksReady, tasks, taskId, projects])

  const parentTask = useMemo(() => {
    if (!tasksReady || !task?.parentId) {
      return null
    }

    return (
      tasks.find(
        (item) =>
          item.id === task.parentId &&
          !item.isDeleted &&
          (workspaceId ? item.envId === workspaceId : true),
      ) ?? null
    )
  }, [tasksReady, tasks, task?.parentId, workspaceId, task])

  const statusList = useMemo<Array<StatusDocument>>(() => {
    if (!statusesReady) {
      return []
    }

    return statuses
  }, [statusesReady, statuses])

  if (!tasksReady || !statusesReady) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
        Loading task...
      </div>
    )
  }

  if (!task) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">Task not found</p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex-1 overflow-hidden">
        <div className="flex h-full justify-center">
          <TaskDetails
            task={task}
            statuses={statusList}
            parentTask={parentTask}
            layout={TASK_DETAILS_LAYOUT.PAGE}
            chrome={chrome}
          />
        </div>
      </div>
    </div>
  )
}

export default TaskDetailsPage
